import { ServiceClient } from '@johnlewispartnership/wtr-website-api-client';

import config from 'config';
import { withApiBaseUrl } from 'services/with-api-base-url';
import { logger } from 'logger';

import type { SummaryRequestBody, SummaryResponse } from './types';

const BASE_URL = withApiBaseUrl(config.services.recipes.path);
const serviceName = 'recipes';

export const defaultSummaryRequestBody: SummaryRequestBody = {
  filters: [],
  search: {
    term: '',
    tags: [[]],
  },
  preferences: {
    size: 24,
    start: 0,
  },
};

type PostRecipesSummaryArgs = {
  body?: Partial<SummaryRequestBody>;
  authHeader?: string;
  clientCorrelationId?: string;
};

class RecipesService extends ServiceClient {
  async postSummary({
    body,
    authHeader,
    clientCorrelationId,
  }: PostRecipesSummaryArgs): Promise<SummaryResponse> {
    const summaryUrl = '/recipes/summary';

    if (!clientCorrelationId) {
      logger.warn({
        payload: {
          message:
            'clientCorrelationId is missing - proceeding without client-correlation-id header',
          service: serviceName,
          url: `${BASE_URL}${summaryUrl}`,
        },
      });
    }

    const requestBody: SummaryRequestBody = {
      ...defaultSummaryRequestBody,
      ...body,
      search: {
        ...defaultSummaryRequestBody.search,
        ...body?.search,
      },
      preferences: {
        ...defaultSummaryRequestBody.preferences,
        ...body?.preferences,
      },
      filters: body?.filters ?? defaultSummaryRequestBody.filters,
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      breadcrumb: config.applicationName,
    };

    if (clientCorrelationId) {
      headers['client-correlation-id'] = clientCorrelationId;
    }

    if (authHeader) {
      headers.Authorization = authHeader;
    }

    const response = await this.httpClient.post<SummaryRequestBody, SummaryResponse>(
      summaryUrl,
      requestBody,
      { headers },
    );

    const correlationId = response.headers?.['wtr-correlation-id'];

    if (response.error) {
      const message = `Failed to fetch recipes summary: ${response.error.message}`;
      logger.error({
        payload: {
          message,
          correlationId,
          httpStatus: String(response.status),
          duration: response.duration,
          service: serviceName,
          url: `${BASE_URL}${summaryUrl}`,
        },
      });

      return Promise.reject(response);
    }

    logger.info({
      payload: {
        message: 'Fetched recipes summary',
        correlationId,
        httpStatus: String(response.status),
        duration: response.duration,
        service: serviceName,
        url: `${BASE_URL}${summaryUrl}`,
      },
    });

    return response.data as SummaryResponse;
  }
}

export default new RecipesService(BASE_URL);
